import React, { forwardRef, useState, useEffect, useCallback } from 'react';
import Spinner from '../Spinner';
import { cn } from '@/utils';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  fullWidth?: boolean;
  // 新增的属性
  autoLoading?: boolean;
  loadingTimeout?: number;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      children,
      disabled,
      size = 'md',
      isLoading = false,
      fullWidth = false,
      autoLoading = true,
      loadingTimeout = 500,
      onClick,
      ...props
    },
    ref
  ) => {
    const [showLoading, setShowLoading] = useState(isLoading);
    const [autoLoadingState, setAutoLoadingState] = useState(false);

    // 合并外部和内部的 loading 状态
    const isButtonLoading = showLoading || autoLoadingState;

    useEffect(() => {
      let timer: NodeJS.Timeout;

      if (isLoading) {
        setShowLoading(true);
      } else {
        timer = setTimeout(() => {
          setShowLoading(false);
        }, loadingTimeout);
      }

      return () => {
        if (timer) {
          clearTimeout(timer);
        }
      };
    }, [isLoading, loadingTimeout]);

    const handleClick = useCallback(
      async (event: React.MouseEvent<HTMLButtonElement>) => {
        if (!onClick || disabled || isButtonLoading) return;

        if (autoLoading) {
          setAutoLoadingState(true);

          try {
            // 处理异步点击事件
            if (onClick.constructor.name === 'AsyncFunction') {
              await (onClick as any)(event);
            } else {
              // 处理同步点击事件
              onClick(event);
            }
          } catch (error) {
            console.error('Button click error:', error);
          } finally {
            // 延迟结束 loading 状态
            setTimeout(() => {
              setAutoLoadingState(false);
            }, loadingTimeout);
          }
        } else {
          // 不启用自动 loading 时直接执行
          onClick(event);
        }
      },
      [onClick, disabled, isButtonLoading, autoLoading, loadingTimeout]
    );

    // 基础样式
    const baseStyles =
      'inline-flex items-center justify-center rounded-[100px] font-medium transition-colors disabled:pointer-events-none disabled:opacity-60';

    // 尺寸样式
    const sizeStyles = {
      sm: 'h-[28px] px-[8px] text-xs',
      md: 'h-[32px] px-[12px] text-sm',
      lg: 'h-[36px] px-[16px] text-base',
    };

    // 宽度样式
    const widthStyle = fullWidth ? 'w-full' : '';

    return (
      <button
        className={cn(baseStyles, sizeStyles[size], widthStyle, className)}
        disabled={disabled || isButtonLoading}
        ref={ref}
        onClick={handleClick}
        {...props}
      >
        {isButtonLoading && <Spinner color="var(--bg-0)" size={12} className="mr-[4px]" />}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
